'use client'

import { AchieveLink } from '@/components/AchieveLink'
import { Typography } from '@/components/Typography'
import { useState, useEffect } from 'react'
import classNames from 'classnames'
import { ArrowRight, ArrowLeft } from 'react-feather'
import { Button, Grid } from '@achieve/ascend'
import { MediaImageStandard } from '@/components/Contentful'
import { useActiveNav } from '../context/ActiveNavContext'

// Shared Contentful entries keep the same id. Keep the first copy so visibility
// and React keys stay unique when a panel is linked from more than one parent.
const dedupeById = (items) => {
  const seen = new Set()

  return (items || []).filter((item) => {
    if (!item?.id) return true
    if (seen.has(item.id)) return false
    seen.add(item.id)
    return true
  })
}

// CTA to the landing page of the section currently shown in the panel
const ViewAllButton = ({ item }) => {
  if (!item?.viewAllLabel || !item?.link) return null

  return (
    <AchieveLink
      href={item.link}
      className="text-black w-fit"
      track={{
        click_id: item.label,
        click_text: item.label,
        click_url: item.link,
        nav_link_section: 'Mega Menu',
        track_event: 'mega_menu',
        event_action: 'link_click',
        event_type: 'link_click',
      }}
    >
      <Button variant="contained" color="primary" size="small">
        <Typography content={item.viewAllLabel} className="labelMd" />
      </Button>
    </AchieveLink>
  )
}

const SubPanelItemsComponent = ({ list }) => {
  const { isActive } = useActiveNav()
  const items = []

  for (const [index, item] of list?.entries() || []) {
    if (item.variant === 'articleCard') {
      // Hidden flydowns now mount on page load. An unresolved or unsupported
      // article has no transformed content and must not crash the menu.
      if (!item.content?.cardTitleText && !item.content?.cardText) continue

      const { cardTitleText, cardImage } = item.content
      const cardText = item.content.cardText ?? ''

      const LinkToArticle = ({ children, className }) => {
        return (
          <AchieveLink
            href={item.link}
            className={classNames('cursor-pointer', className)}
            aria-current={isActive(item) ? 'page' : undefined}
            track={{
              click_id: item.label,
              click_text: item.label,
              click_url: item.link,
              nav_link_section: 'Mega Menu',
              track_event: 'mega_menu',
              event_action: 'link_click',
              event_type: 'link_click',
            }}
          >
            {children}
          </AchieveLink>
        )
      }

      items.push(
        <Grid key={`${item.id}-${index}`} size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
          <LinkToArticle>
            <div className="flex flex-col gap-3">
              <div className="overflow-hidden">
                <MediaImageStandard
                  src={cardImage}
                  alt={cardTitleText}
                  objectFit="cover"
                  width={200}
                  height={150}
                  className="rounded-md"
                />
              </div>
              <Typography
                content={cardTitleText}
                className={classNames('labelMd font-bold!', {
                  'text-blue-900': isActive(item),
                  'text-gray-600': !isActive(item),
                })}
              />
              <div>
                <Typography
                  content={cardText.length > 50 ? `${cardText.substring(0, 50)}...` : cardText}
                  className="bodySm text-gray-500"
                />
              </div>
            </div>
          </LinkToArticle>
        </Grid>
      )
    }
  }

  return (
    <Grid container spacing={3} className="w-full">
      {items}
    </Grid>
  )
}

const TwoPanelNavigation = ({ data }) => {
  const { isActive, isActiveBranch } = useActiveNav()

  // Flatten panelItemGroup for first level display
  const flattenFirstLevel = (items) => {
    const flattened = []
    for (const item of items) {
      if (
        item.variant === 'panelItemGroup' ||
        (!item.label && item.children && item.children.length > 0)
      ) {
        flattened.push(...flattenFirstLevel(item.children))
      } else if (item.label) {
        flattened.push(item)
      }
    }
    return flattened
  }

  // Keep second-level sections separated by parent group.
  const flattenSectionItems = (items) => {
    const flattened = []
    for (const item of items || []) {
      if (
        item.variant === 'panelItemGroup' ||
        (!item.label && item.children && item.children.length > 0)
      ) {
        flattened.push(...flattenSectionItems(item.children))
      } else if (item.label) {
        flattened.push(item)
      }
    }
    return flattened
  }

  const getSecondLevelSections = (items) => {
    const sections = []

    for (const [index, item] of (items || []).entries()) {
      if (!item) continue

      const isGroup =
        item.variant === 'panelItemGroup' ||
        (!item.label && item.children && item.children.length > 0)

      if (isGroup) {
        const sectionItems = flattenSectionItems(item.children || [])
        if (sectionItems.length > 0) {
          sections.push({
            id: item.id || `section-${index}`,
            label: item.label || null,
            items: sectionItems,
          })
        }
      } else if (item.label) {
        sections.push({
          id: item.id || `section-${index}`,
          label: null,
          items: [item],
        })
      }
    }

    return sections
  }

  // Find original item by id
  const findOriginalItem = (itemId, items) => {
    for (const item of items) {
      if (item.id === itemId) {
        return item
      }
      if (item.children && item.children.length > 0) {
        const found = findOriginalItem(itemId, item.children)
        if (found) return found
      }
    }
    return null
  }

  const getNavigablePanels = (items) => {
    const panels = []

    for (const item of items || []) {
      const isGroup =
        item.variant === 'panelItemGroup' ||
        (!item.label && item.children && item.children.length > 0)

      if (isGroup) {
        panels.push(...getNavigablePanels(item.children))
      } else if (item.label && item.children?.length > 0) {
        panels.push(item, ...getNavigablePanels(item.children))
      }
    }

    return dedupeById(panels)
  }

  const initialCandidates = flattenFirstLevel(data.children)
    .map((item) => findOriginalItem(item.id, data.children) || item)
    .filter((item) => item.children?.length > 0)
  const initialSelectedFirstLevel =
    initialCandidates.find((item) => isActiveBranch(item)) || initialCandidates[0] || null

  // selectedFirstLevel: the item whose children are shown in the right panel
  const [selectedFirstLevel, setSelectedFirstLevel] = useState(initialSelectedFirstLevel)
  // leftPanelList: the list to show in the left panel (parent level of selectedFirstLevel)
  const [leftPanelList, setLeftPanelList] = useState(null)
  const [navigationHistory, setNavigationHistory] = useState([])

  // Handle click on first level (left panel)
  const handleFirstLevelClick = (item) => {
    // Find the item in the current leftPanelList or root
    const searchList = leftPanelList || data.children
    const originalItem = findOriginalItem(item.id, searchList) || item
    if (originalItem.children && originalItem.children.length > 0) {
      setSelectedFirstLevel(originalItem)
    }
  }

  // Handle click on second level (right panel)
  const handleSecondLevelClick = (item) => {
    const originalItem = findOriginalItem(item.id, selectedFirstLevel?.children || []) || item
    if (originalItem.children && originalItem.children.length > 0) {
      // Save current state to history
      setNavigationHistory((prev) => [
        ...prev,
        {
          leftPanelList: leftPanelList,
          selectedFirstLevel: selectedFirstLevel,
        },
      ])
      // The clicked item's parent level becomes the new left panel
      // The clicked item becomes the new selectedFirstLevel (its children go to right)
      setLeftPanelList(selectedFirstLevel?.children || [])
      setSelectedFirstLevel(originalItem)
    }
  }

  // Handle back button
  const handleBack = () => {
    if (navigationHistory.length > 0) {
      const previousState = navigationHistory[navigationHistory.length - 1]
      setNavigationHistory((prev) => prev.slice(0, -1))
      // Restore previous state
      setLeftPanelList(previousState.leftPanelList)
      setSelectedFirstLevel(previousState.selectedFirstLevel)
    }
  }

  // Initialize selected first level if not set. The branch holding the current
  // page wins so the panel opens on the section the user is already browsing.
  useEffect(() => {
    if (!selectedFirstLevel && data.children && data.children.length > 0) {
      if (initialSelectedFirstLevel) {
        setSelectedFirstLevel(initialSelectedFirstLevel)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.children])

  // Left panel shows: leftPanelList if set, otherwise data.children (root level)
  const firstLevelList = leftPanelList
    ? flattenFirstLevel(leftPanelList)
    : flattenFirstLevel(data.children)
  const navigablePanels = getNavigablePanels(data.children)

  return (
    <div
      data-testid="mega-menu-subnav-panel-list-section"
      className="flex flex-row gap-2 w-[1000px]"
    >
      <div className="flex flex-col gap-2 p-8 bg-blue-25 border-solid border-0 border-r border-gray-300 w-[350px]">
        {navigationHistory.length > 0 && (
          <AchieveLink noLink onClick={handleBack} className="text-black mb-2 cursor-pointer">
            <div className={classNames('flex flex-row gap-2 items-center')}>
              <ArrowLeft className="w-4 h-4" />
              <Typography content="Back" className="labelMd" />
            </div>
          </AchieveLink>
        )}
        {firstLevelList.map((item, index) => {
          const hasChildren = Array.isArray(item.children) && item.children.length > 0
          const shouldUseLink = !hasChildren && !!item.link
          // Check if item is selected - need to search in the correct list
          const searchList = leftPanelList || data.children
          const originalItem = findOriginalItem(item.id, searchList) || item
          const isSelected = selectedFirstLevel?.id === originalItem.id
          // The entry that holds the page the user is on
          const isCurrent = isActiveBranch(originalItem)

          return (
            <div
              key={`${item.id || index}-${index}`}
              className={classNames(
                'flex flex-col gap-2 py-2 border-solid border-gray-300 border-0 border-b',
                {
                  'last:border-b-0': index === firstLevelList.length - 1,
                }
              )}
            >
              {hasChildren ? (
                <div
                  onClick={() => handleFirstLevelClick(item)}
                  className="text-black cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleFirstLevelClick(item)
                    }
                  }}
                >
                  <div className={classNames('flex flex-row gap-2 items-center')}>
                    <Typography
                      content={item.label}
                      className={classNames('labelMd whitespace-nowrap', {
                        'font-bold! text-blue-900': isCurrent,
                        'font-bold! text-[#154199]': isSelected && !isCurrent,
                      })}
                    />
                    <ArrowRight
                      className={classNames('w-4 h-4', {
                        'text-blue-900!': isCurrent,
                        'text-[#154199]!': isSelected && !isCurrent,
                      })}
                    />
                  </div>
                </div>
              ) : shouldUseLink ? (
                <AchieveLink
                  href={item.link}
                  className="text-black"
                  aria-current={isCurrent ? 'page' : undefined}
                  track={{
                    click_id: item.label,
                    click_text: item.label,
                    click_url: item.link,
                    nav_link_section: 'Mega Menu',
                    track_event: 'mega_menu',
                    event_action: 'link_click',
                    event_type: 'link_click',
                  }}
                >
                  <div className={classNames('flex flex-row gap-2 items-center')}>
                    <Typography
                      content={item.label}
                      className={classNames('labelMd whitespace-nowrap', {
                        'text-blue-900 font-bold!': isCurrent,
                      })}
                    />
                  </div>
                </AchieveLink>
              ) : (
                <div className={classNames('flex flex-row gap-2 items-center')}>
                  <Typography content={item.label} className="labelMd whitespace-nowrap" />
                </div>
              )}
            </div>
          )
        })}
      </div>
      {navigablePanels.map((panel) => {
        const hidden = selectedFirstLevel?.id !== panel.id
        const secondLevelSections = getSecondLevelSections(panel.children || [])
        const secondLevelColumns = Number(panel.columns) > 0 ? Number(panel.columns) : 1

        return (
          <div
            key={panel.id}
            className={classNames('flex flex-col gap-6 p-8 w-full', { hidden })}
            aria-hidden={hidden || undefined}
            inert={hidden || undefined}
            data-testid={`mega-menu-panel-${panel.id}`}
          >
            <Typography content={panel.label} className="labelMd text-gray-500 font-bold!" />
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: `repeat(${secondLevelColumns}, minmax(0, 1fr))` }}
            >
              {secondLevelSections.map((section, sectionIndex) => (
                <div key={`${section.id}-${sectionIndex}`} className="flex flex-col gap-4">
                  {section.label && (
                    <Typography
                      content={section.label}
                      className="labelSm text-gray-500 font-bold!"
                    />
                  )}
                  {section.items.map((item, itemIndex) => {
                    const hasChildren = Array.isArray(item.children) && item.children.length > 0
                    const shouldUseLink = !hasChildren && !!item.link
                    const shouldNavigate = hasChildren
                    const isCurrent = isActiveBranch(item)
                    const labelClassName = classNames('labelMd', {
                      'text-blue-900 font-bold!': isCurrent,
                    })

                    const handleClick = (e) => {
                      if (shouldNavigate) {
                        e.preventDefault()
                        e.stopPropagation()
                        handleSecondLevelClick(item)
                      }
                    }

                    return (
                      <div
                        key={`${item.id || itemIndex}-${itemIndex}`}
                        className={classNames(
                          'flex flex-col gap-2 pb-2 px-4 py-2 hover:bg-blue-25 rounded-md hover:underline-none',
                          { 'bg-blue-50': isCurrent }
                        )}
                      >
                        {shouldNavigate ? (
                          <div
                            onClick={handleClick}
                            className="text-black cursor-pointer"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                handleSecondLevelClick(item)
                              }
                            }}
                          >
                            <div className={classNames('flex flex-row gap-2 items-center')}>
                              <Typography content={item.label} className={labelClassName} />
                              <ArrowRight
                                className={classNames('w-4 h-4', { 'text-blue-900!': isCurrent })}
                              />
                            </div>
                          </div>
                        ) : shouldUseLink ? (
                          <AchieveLink
                            href={item.link}
                            className="text-black"
                            aria-current={isActive(item) ? 'page' : undefined}
                            track={{
                              click_id: item.label,
                              click_text: item.label,
                              click_url: item.link,
                              nav_link_section: 'Mega Menu',
                              track_event: 'mega_menu',
                              event_action: 'link_click',
                              event_type: 'link_click',
                            }}
                          >
                            <div className={classNames('flex flex-row gap-2 items-center')}>
                              <Typography content={item.label} className={labelClassName} />
                            </div>
                          </AchieveLink>
                        ) : (
                          <div className={classNames('flex flex-row gap-2 items-center')}>
                            <Typography content={item.label} className={labelClassName} />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
            <ViewAllButton item={panel} />
          </div>
        )
      })}
    </div>
  )
}

const RecursivePanel = ({ list, level = 1 }) => {
  const { isActive, isActiveBranch } = useActiveNav()
  const [currentList, setCurrentList] = useState(list || [])
  // currentParent: the item whose children are being listed, holds the view all link
  const [currentParent, setCurrentParent] = useState(null)
  const [navigationHistory, setNavigationHistory] = useState([])

  // Sync currentList when list prop changes
  useEffect(() => {
    if (list) {
      setCurrentList(list)
      setCurrentParent(null)
      setNavigationHistory([])
    }
  }, [list])

  // Calculate current level based on navigation history
  // level starts at 1, and each navigation adds 1
  const currentLevel = level + navigationHistory.length

  // Flatten the list to handle panelItemGroup and other intermediate levels
  // This only flattens for display, but we need to find the original item for navigation
  const flattenList = (items) => {
    const flattened = []
    for (const item of items) {
      // If it's a panelItemGroup (or similar container without label), use its children directly
      if (
        item.variant === 'panelItemGroup' ||
        (!item.label && item.children && item.children.length > 0)
      ) {
        // Recursively flatten the children, but keep reference to original item
        const childrenItems = flattenList(item.children)
        flattened.push(...childrenItems)
      } else if (item.label) {
        // Only include items that have a label
        flattened.push(item)
      }
    }
    return flattened
  }

  // Find the original item in the currentList (before flattening) by id
  const findOriginalItem = (flattenedItem) => {
    const findInList = (items) => {
      for (const item of items) {
        if (item.id === flattenedItem.id) {
          return item
        }
        if (item.children && item.children.length > 0) {
          const found = findInList(item.children)
          if (found) return found
        }
      }
      return null
    }
    return findInList(currentList)
  }

  const handleItemClick = (item) => {
    // Find the original item to get the correct children structure
    const originalItem = findOriginalItem(item) || item
    if (originalItem.children && originalItem.children.length > 0) {
      // Navigate to children level
      setNavigationHistory((prev) => [...prev, { list: currentList, parent: currentParent }])
      setCurrentList(originalItem.children)
      setCurrentParent(originalItem)
    }
  }

  const handleBack = () => {
    if (navigationHistory.length > 0) {
      const previousState = navigationHistory[navigationHistory.length - 1]
      setNavigationHistory((prev) => prev.slice(0, -1))
      setCurrentList(previousState.list)
      setCurrentParent(previousState.parent)
    }
  }

  const showBackButton = navigationHistory.length > 0 || level > 1

  // Get the flattened list for display
  const displayList = flattenList(currentList)

  return (
    <div
      data-testid="mega-menu-subnav-panel-section"
      className="flex flex-col gap-4 w-screen bg-blue-25 px-10 py-6"
    >
      {showBackButton && (
        <AchieveLink noLink onClick={handleBack} className="text-black">
          <div className={classNames('flex flex-row gap-2 items-center mb-4')}>
            <ArrowLeft className="w-4 h-4" />
            <Typography content="Back" className="labelMd" />
          </div>
        </AchieveLink>
      )}
      {displayList.map((child, index) => {
        const hasChildren = Array.isArray(child.children) && child.children.length > 0
        // Only use link if there are NO children
        const shouldUseLink = !hasChildren && !!child.link
        const shouldNavigate = hasChildren
        const labelClassName = classNames('labelMd', {
          'text-blue-900 font-bold!': isActiveBranch(child),
        })

        const handleClick = (e) => {
          if (shouldNavigate) {
            e.preventDefault()
            e.stopPropagation()
            handleItemClick(child)
          }
        }

        return (
          <div
            key={`${child.id || index}-${index}`}
            className={classNames('flex flex-col gap-2 pb-2', {
              'border-solid border-gray-300 border-0 border-b pb-4': currentLevel === 1,
              'last:border-b-0': currentLevel === 1 && index === displayList.length - 1,
            })}
          >
            {shouldNavigate ? (
              <div
                onClick={handleClick}
                className="text-black cursor-pointer"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleItemClick(child)
                  }
                }}
              >
                <div className={classNames('flex flex-row gap-2 items-center')}>
                  <Typography content={child.label} className={labelClassName} />
                  <ArrowRight
                    className={classNames('w-4 h-4', {
                      'text-blue-900!': isActiveBranch(child),
                    })}
                  />
                </div>
              </div>
            ) : shouldUseLink ? (
              <AchieveLink
                href={child.link}
                key={`${child.id}`}
                className="text-black"
                aria-current={isActive(child) ? 'page' : undefined}
                track={{
                  click_id: child.label,
                  click_text: child.label,
                  click_url: child.link,
                  nav_link_section: 'Mega Menu',
                  track_event: 'mega_menu',
                  event_action: 'link_click',
                  event_type: 'link_click',
                }}
              >
                <div className={classNames('flex flex-row gap-2 items-center')}>
                  <Typography content={child.label} className={labelClassName} />
                </div>
              </AchieveLink>
            ) : (
              <div className={classNames('flex flex-row gap-2 items-center')}>
                <Typography content={child.label} className={labelClassName} />
              </div>
            )}
          </div>
        )
      })}
      <ViewAllButton item={currentParent} />
    </div>
  )
}

export const SubnavPanelSection = ({ isMobile, data, setHoverSubnav }) => {
  const variant = data.variant
  const { isActive, isActiveBranch } = useActiveNav()
  const initialParent = data.children?.find((child) => isActiveBranch(child)) ?? data.children?.[0]
  const initialSubPanel =
    initialParent?.children?.find((child) => isActiveBranch(child)) ??
    initialParent?.children?.[0] ??
    null
  const [subPanel, setSubPanel] = useState(initialSubPanel)

  useEffect(() => {
    if (variant === 'panelList' && !isMobile && subPanel === null) {
      setSubPanel(initialSubPanel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, variant, subPanel, isMobile])

  if (variant === 'panelList') {
    if (isMobile) {
      return (
        <div
          data-testid="mega-menu-subnav-default-section-mobile"
          className="flex flex-col gap-4 w-screen bg-blue-25 px-10 py-6"
        >
          {data?.children?.map((child, index) => (
            <div
              key={`${data.id}-${child.id}`}
              className={classNames(
                'flex flex-col gap-2 pb-4 border-solid border-gray-300 border-0 border-b',
                {
                  'last:border-b-0': index === data.children.length - 1,
                }
              )}
            >
              <AchieveLink
                href={child.link}
                key={`${data.id}-${child.id}-${child.id}`}
                className="text-black"
                aria-current={isActive(child) ? 'page' : undefined}
                track={{
                  click_id: child.label,
                  click_text: child.label,
                  click_url: child.link,
                  nav_link_section: 'Mega Menu',
                  track_event: 'mega_menu',
                  event_action: 'link_click',
                  event_type: 'link_click',
                }}
              >
                <div className={classNames('flex flex-row gap-2 items-center')}>
                  <Typography
                    content={child.label}
                    className={classNames('labelMd', {
                      'text-blue-900 font-bold!': isActiveBranch(child),
                    })}
                  />
                </div>
              </AchieveLink>
            </div>
          ))}
        </div>
      )
    } else {
      const panels = dedupeById(
        data.children?.flatMap((child) => child.children || []).filter(Boolean)
      )

      return (
        <div
          data-testid="mega-menu-subnav-panel-list-section"
          className="flex flex-row gap-2 w-[1000px]"
        >
          <div className="flex flex-col gap-2 p-8 bg-blue-25 border-solid border-0 border-r border-gray-300 w-[350px]">
            {data?.children?.map((child, index) => (
              <div
                key={`${data.id}-${child.id}`}
                className={classNames(
                  'flex flex-col gap-2 py-2 border-solid border-gray-300 border-0 border-b',
                  {
                    'last:border-b-0': index === data.children.length - 1,
                  }
                )}
              >
                <AchieveLink
                  noLink={!!child.children?.[0]}
                  href={child.link}
                  key={`${data.id}-${child.id}-${child.id}`}
                  className="text-black cursor-pointer"
                  aria-current={isActive(child) ? 'page' : undefined}
                  onClick={() => {
                    if (!child.children?.[0]) {
                      setHoverSubnav(false)
                      return
                    }
                    setSubPanel(child.children?.[0])
                  }}
                >
                  <div className={classNames('flex flex-row gap-2 items-center')}>
                    <Typography
                      content={child.label}
                      className={classNames('labelMd whitespace-nowrap', {
                        'font-bold! text-blue-900': isActiveBranch(child),
                        'font-bold! text-[#154199]':
                          !isActiveBranch(child) &&
                          !!subPanel?.id &&
                          subPanel.id === child.children?.[0]?.id,
                      })}
                    />
                    {child.children?.[0] && (
                      <ArrowRight
                        className={classNames('w-4 h-4', {
                          'text-blue-900!': isActiveBranch(child),
                        })}
                      />
                    )}
                  </div>
                </AchieveLink>
              </div>
            ))}
          </div>
          {panels.map((panel) => {
            const hidden = subPanel?.id !== panel.id

            return (
              <div
                key={panel.id}
                className={classNames('flex flex-col gap-6 px-4 py-8', { hidden })}
                aria-hidden={hidden || undefined}
                inert={hidden || undefined}
                data-testid={`mega-menu-panel-${panel.id}`}
              >
                <Typography content={panel.label} className="labelMd text-gray-500 font-bold!" />
                <SubPanelItemsComponent list={panel.children} />
                <ViewAllButton item={panel} />
              </div>
            )
          })}
        </div>
      )
    }
  } else if (variant === 'panel') {
    if (isMobile) {
      return <RecursivePanel list={data.children} />
    } else {
      return <TwoPanelNavigation data={data} />
    }
  }
}

export default SubnavPanelSection
